<template>
  <div class="connect__wallet-image">
    <img alt="wallet" src="@/assets/connect.svg" />
  </div>
  <p class="mb-4 text-center">
    {{ $t('dialogs.needToConnectWallet') }}
  </p>
  <PButton
    :key="pontemWallet?.name"
    class="connect w-full mb-2"
    label="Connect"
    @click="connectWallet(pontemWallet)"
  >
    <img
      :alt="pontemWallet?.name"
      :src="pontemWallet?.icon"
      class="wallet-logo mr-3"
    />
    <div class="connect__description">
      <div class="connect__title">
        <span>{{ pontemWallet?.name || '' }}</span>
        <div class="connect__caption">
          {{ $t('dialogs.auditedBy') }}
          <img
            alt="Halborn"
            class="connect__auditor"
            src="@/assets/auditors/halborn.svg"
          />
        </div>
      </div>
      <img
        alt="recommended"
        class="connect__badge"
        src="@/assets/wallets/recommended.svg"
      />
    </div>
  </PButton>
  <PButton
    v-if="nightlyWallet && isMovement"
    :key="nightlyWallet?.name"
    class="connect w-full mb-2"
    label="Connect"
    @click="connectWallet(nightlyWallet)"
  >
    <img
      :alt="nightlyWallet?.name"
      :src="nightlyWallet?.icon"
      class="wallet-logo mr-3"
    />
    <div class="connect__description">
      <div class="connect__title">
        <span>{{ nightlyWallet?.name || '' }}</span>
      </div>
      <img
        alt="recommended"
        class="connect__badge"
        src="@/assets/wallets/recommended.svg"
      />
    </div>
  </PButton>
  <div class="block--scrollable p-overflow-auto">
    <PButton
      :key="'other wallets'"
      class="connect w-full mb-2 other-wallets__button"
      label="Connect"
      @click="handleToggle"
    >
      <span class="chevron mr-3">
        <i
          :class="[isToggled ? 'pi-chevron-up' : 'pi-chevron-down']"
          class="pi pi-chevron-down"
        />
      </span>
      {{ $t('dialogs.otherWallets') }}
    </PButton>
    <div v-if="isToggled" class="other-wallets__block">
      <PButton
        v-for="wallet in otherWallets"
        :key="wallet.name"
        class="connect w-full mb-2"
        label="Connect"
        @click="connectWallet(wallet)"
      >
        <img :alt="wallet.name" :src="wallet.icon" class="wallet-logo mr-3" />
        {{ wallet.name }}
        <div v-if="isDetected(wallet?.name)" class="other-wallets__detected">
          {{ $t('dialogs.detected') }}
        </div>
      </PButton>
    </div>
    <div v-if="error" class="p-error mb-3 text-center text-sm">
      {{ error }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, ref } from 'vue';
import { useStore } from '@/store';
import { CORRECT_CHAIN, ChainsEnum } from '@/constants';
import { CHAIN_ID_TO_SWITCH_NETWORK_ID } from '@/constants/networks';
import { Wallet } from '@aptos-labs/wallet-adapter-vue';
import { useWalletPlugin } from '@/composables/useWalletPlugin';

const dialogRef = inject('dialogRef');

const { isMobile } = useStore();

const { account, wallets: adapterWallets, connect } = useWalletPlugin();

type IProps = {
  visible?: boolean;
};

const props = defineProps<IProps>();
const emits = defineEmits(['update:visible']);

const isToggled = ref<boolean>(false);

const isMovement = computed(() => {
  return [
    ChainsEnum['movement-mainnet'],
    ChainsEnum['movement-testnet'],
  ].includes(CORRECT_CHAIN.id);
});

const pontemWallet = computed(() => {
  return adapterWallets?.value?.find(({ name }) =>
    name.toLowerCase().includes('pontem'),
  );
});

const nightlyWallet = computed(() => {
  return adapterWallets?.value?.find(({ name }) =>
    name.toLowerCase().includes('nightly'),
  );
});

const availableWallets = computed(() => {
  return (
    adapterWallets?.value.filter(
      (wallet) => wallet.readyState === 'Installed',
    ) || []
  );
});

function isDetected(name: string) {
  /* Trust wallet should be detected as petra(aptos) only on mobile devices */
  if (
    isMobile.value &&
    availableWallets?.value?.some((adapter) => adapter.name === 'Aptos')
  ) {
    switch (name) {
      case 'Trust':
        return true;
      case 'Aptos':
        return false;
    }
  }
  if (!isMobile.value && name === 'Trust') return false;
  return availableWallets.value?.some((adapter) => adapter.name === name);
}

const handleToggle = () => {
  isToggled.value = !isToggled.value;
};

const otherWallets = computed(() => {
  return adapterWallets?.value.filter(({ name }) => {
    /* hide trust wallet for desktop mode, it opens petra wallet */
    if (!isMobile.value && name === 'Trust') {
      return false;
    }
    if (isMovement.value && name === 'Nightly') {
      return false;
    }
    return !name.toLowerCase().includes('pontem');
  });
});

const displayModal = computed({
  get: () => !!props.visible,
  set: (v) => emits('update:visible', v),
});

const isLoading = ref(false);
const error = ref(undefined);

onBeforeUnmount(() => {
  isLoading.value = false;
  error.value = undefined;
  isToggled.value = false;
});

async function connectWallet(wallet: Wallet) {
  try {
    isLoading.value = true;
    error.value = undefined;
    if (
      wallet.name.toLowerCase().includes('pontem') &&
      window.pontem?._client?._rpcClient?._options?.streamName ===
        'pontem-external'
    ) {
      await window.pontem.switchNetwork(
        CHAIN_ID_TO_SWITCH_NETWORK_ID[CORRECT_CHAIN.id],
      );

      if (pontemWallet.value) {
        await connect(pontemWallet.value.name);
      }
    } else {
      await connect(wallet.name);
    }
    if (account.value) {
      displayModal.value = false;
      error.value = undefined;
      isToggled.value = false;
    }
  } catch (e: any) {
    console.log('error connect wallet ', e);
    error.value = e.message;
  } finally {
    isLoading.value = false;
    dialogRef.value.close();
  }
}
</script>

<style lang="scss" scoped>
.text {
  margin-top: 0;
  color: rgba(251, 250, 255, 0.6);
}

.image-link {
  display: block;
}

.help {
  color: transparentize(#e5e4fa, 0.6);
  font-size: 13px;
  font-weight: 500;

  a {
    color: transparentize(#e5e4fa, 0.2);

    &:hover {
      text-decoration: underline;
    }
  }
}

.modal__title {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  text-align: center;
  width: 100%;
  margin: 0 0 0 16px;
}

.connect {
  width: min-content;
  margin: 0;
  border-radius: 12px;
  background-color: #28253e;
  min-height: 64px;
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
  animation: fade ease-in-out 0.15s;
  &:hover {
    background-color: #33284f;
    border: none;
    outline: none;
    color: white;
  }
  &:focus {
    box-shadow: 0 0 0 2px #1c2127, 0 0 0 2px rgb(196 181 253 / 70%) inset,
      0 1px 2px 0 rgb(0 0 0 / 0%);
  }
  border: none;
  outline: none;
  &__description {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
  }
  &__title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  &__caption {
    font-size: 13px;
    line-height: 24px;
    color: #9594aa;
    font-weight: 400;
    text-align: left;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  &__badge {
    width: 109px;
  }
  &__wallet-image {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
  }
  &__auditor {
    width: 82px;
    margin-left: 0.25em;
  }

  @media screen and (max-width: 400px) {
    &__auditor {
      width: 70px;
    }
    &__badge {
      width: 89px;
    }
  }
}

.connect-dialog {
  align-items: flex-start;
}

.divider {
  opacity: 0.15;
  border-bottom: 1px solid var(--text-color);
  margin: 0 -24px 24px;
}

.store__link {
  display: flex;
  margin-bottom: 24px;
}

.wallet-logo {
  height: 36px;
  width: auto;
}

.chevron {
  border-radius: 50px;
  height: 36px;
  width: 36px;
  background-color: #3e3e52;
  display: flex;
  align-items: center;
  justify-content: center;
}

.other-wallets {
  &__detected {
    font-size: 11px;
    letter-spacing: 0.08em;
    color: var(--text-color);
    opacity: 0.6;
    line-height: 24px;
    font-weight: 900;
    margin-left: auto;
  }
  &__button:focus {
    box-shadow: none;
  }
}

.block--scrollable {
  overflow-y: auto;
  height: 100%;
  max-height: calc(100vh - 451px);
}

@keyframes fade {
  0% {
    opacity: 0;
    transform: translate(0, -50%);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0);
  }
}
</style>
